import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { contentService } from "../services/contentService"; // Import service
import SEO from "../components/SEO";

const About = () => {
  // Setup state for dynamic content
  const [content, setContent] = useState(null);

  // Fetch content on load
  useEffect(() => {
    const fetchContent = async () => {
      try {
        const data = await contentService.getPageContent("about");
        if (data) setContent(data);
      } catch (error) {
        console.error("Failed to fetch about content:", error);
      }
    };
    fetchContent();
  }, []);

  // Use content from DB if available, otherwise use defaults
  const text = {
    h1: content?.h1 || "About ACF Luxury Stays",
    introCopy:
      content?.introCopy ||
      "At ACF Luxury Stays, we believe that true luxury isn’t just found in high-end finishes—it’s found in the peace of mind that comes from meticulous, personal oversight. Our boutique brand was born from a deep appreciation for the rugged alpine majesty of the Colorado Rockies and the sun-drenched, sophisticated shores of Southern California. We offer a deliberately small collection designed for travelers who seek a sanctuary curated with intentionality and care.",
    h2_family: content?.h2_family || "Family Owned & Intentionally Operated",
    familyCopy:
      content?.familyCopy ||
      "We aren’t a large-scale management agency; we are family owners who have chosen to call these properties our own. This intentional approach allows us to bridge the gap between the professional accountability of a luxury hotel and the authentic warmth of a private home.",
    standard_h2: content?.standard_h2 || "The ACF Standard",
    card1_title: content?.card1_title || "Personal Oversight",
    card1_text:
      content?.card1_text ||
      "Every home in our collection is personally overseen by our family team to ensure it meets our exacting standards. We don't rely on remote third parties; we are hands-on.",
    card2_title: content?.card2_title || "Hotel-Level Excellence",
    card2_text:
      content?.card2_text ||
      "We maintain a rigorous commitment to professional-grade cleaning and maintenance, ensuring your home is pristine, stocked, and ready upon your arrival.",
    card3_title: content?.card3_title || "Curated Details",
    card3_text:
      content?.card3_text ||
      "From the high-end chef’s kitchens to the artwork on our walls, every element is chosen to elevate your experience and create an atmosphere of refined comfort.",
    card4_title: content?.card4_title || "Direct Accountability",
    card4_text:
      content?.card4_text ||
      "Because we own and operate our homes, we are directly invested in the success of your stay and the long-term care of our properties. You deal with us, not a call center.",
  };

  return (
    <div className="pt-32 pb-20">
      <SEO
        title="About ACF Luxury Stays | Boutique Luxury Vacation Rentals"
        description="Meet ACF Luxury Stays: A family-owned boutique hospitality brand."
        url="/about"
      />

      <div className="max-w-4xl mx-auto px-4 text-center">
        <h1 className="text-4xl md:text-5xl font-primary text-acf-blue mb-8">
          {text.h1}
        </h1>
        <div className="w-24 h-1 bg-acf-gold mx-auto mb-8"></div>
        <p className="font-secondary text-lg text-gray-600 leading-relaxed mb-16">
          {text.introCopy}
        </p>

        {/* New Image Section */}
        <div className="mb-8">
          <img
            src="/images/about-family.jpg"
            alt="The ACF Family"
            className="w-full h-[400px] object-cover rounded-sm shadow-md"
            onError={(e) => {
              e.target.style.display = "none";
            }}
          />
        </div>

        <div className="mb-6 text-center">
          <h2 className="text-3xl font-primary text-acf-blue mb-2">
            Family Owned & Intentionally Operated by A. Chris Fawzy
          </h2>
          <div className="w-16 h-1 bg-acf-gold mx-auto"></div>
        </div>

        <div className="bg-acf-slate p-12 mb-16">
          <div className="flex flex-col md:flex-row items-stretch gap-8 -m-12">
            <img
              src="/images/AC_Headshot.png"
              alt="A. Chris Fawzy"
              className="w-full md:w-1/2 object-cover flex-shrink-0"
            />
            <div className="text-left flex flex-col justify-center py-12 pr-12">
              <p className="font-secondary text-gray-600 mb-0">{text.familyCopy}</p>
            </div>
          </div>
        </div>

        {/* The ACF Standard Grid */}
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-primary text-acf-blue mb-6">
            {text.standard_h2}
          </h2>
          <div className="w-16 h-1 bg-acf-gold mx-auto"></div>
        </div>

        <div className="grid md:grid-cols-2 gap-8 text-left mb-16">
          {/* Card 1 */}
          <div className="bg-white p-8 shadow-lg border-t-4 border-acf-blue hover:shadow-xl transition-shadow duration-300 rounded-sm">
            <div className="text-4xl mb-4">👁️</div>
            <h3 className="font-primary text-acf-blue text-xl mb-3">
              {text.card1_title}
            </h3>
            <p className="font-secondary text-gray-600 text-sm leading-relaxed">
              {text.card1_text}
            </p>
          </div>

          {/* Card 2 */}
          <div className="bg-white p-8 shadow-lg border-t-4 border-acf-gold hover:shadow-xl transition-shadow duration-300 rounded-sm">
            <div className="text-4xl mb-4">✨</div>
            <h3 className="font-primary text-acf-blue text-xl mb-3">
              {text.card2_title}
            </h3>
            <p className="font-secondary text-gray-600 text-sm leading-relaxed">
              {text.card2_text}
            </p>
          </div>

          {/* Card 3 */}
          <div className="bg-white p-8 shadow-lg border-t-4 border-acf-gold hover:shadow-xl transition-shadow duration-300 rounded-sm">
            <div className="text-4xl mb-4">🎨</div>
            <h3 className="font-primary text-acf-blue text-xl mb-3">
              {text.card3_title}
            </h3>
            <p className="font-secondary text-gray-600 text-sm leading-relaxed">
              {text.card3_text}
            </p>
          </div>

          {/* Card 4 */}
          <div className="bg-white p-8 shadow-lg border-t-4 border-acf-blue hover:shadow-xl transition-shadow duration-300 rounded-sm">
            <div className="text-4xl mb-4">🤝</div>
            <h3 className="font-primary text-acf-blue text-xl mb-3">
              {text.card4_title}
            </h3>
            <p className="font-secondary text-gray-600 text-sm leading-relaxed">
              {text.card4_text}
            </p>
          </div>
        </div>

        <Link
          to="/contact"
          className="bg-acf-blue text-white px-8 py-3 font-primary uppercase tracking-widest hover:bg-acf-gold transition duration-300 no-underline"
        >
          Contact Our Team
        </Link>
      </div>
    </div>
  );
};

export default About;
